import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import {
  estimatePushAudience,
  type PushAudienceFilters,
  type PushAudienceType,
  type PushCampaignEstimate,
} from '../../services/resortAdminApi';
import { getPushCampaignFailureMessage, toPushEstimateRequest } from '../../utils/pushCampaignValidation';

const ESTIMATE_DEBOUNCE_MS = 300;

interface EstimateCardProps {
  audienceType: PushAudienceType;
  filters: PushAudienceFilters;
  campaignName: string | null;
  onEstimateChange: (estimate: PushCampaignEstimate | null) => void;
}

export const EstimateCard: React.FC<EstimateCardProps> = props => {
  const [estimate, setEstimate] = useState<PushCampaignEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestSequenceRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const onEstimateChangeRef = useRef(props.onEstimateChange);

  useEffect(() => {
    onEstimateChangeRef.current = props.onEstimateChange;
  }, [props.onEstimateChange]);

  const audienceType = props.audienceType;
  const campaignName = props.campaignName;
  const needsSavedDraft = audienceType === 'test_list' && campaignName == null;
  const request = useMemo(
    () => toPushEstimateRequest(audienceType, props.filters, campaignName),
    [audienceType, props.filters, campaignName]
  );
  const requestKey = JSON.stringify(request);
  const requestRef = useRef(request);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    if (needsSavedDraft) {
      setEstimate(null);
      setError(null);
      setIsLoading(false);
      onEstimateChangeRef.current(null);
      return;
    }

    const timer = setTimeout(() => {
      requestSequenceRef.current += 1;
      const sequence = requestSequenceRef.current;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setIsLoading(true);
      estimatePushAudience(requestRef.current, controller.signal)
        .then(x => {
          if (sequence !== requestSequenceRef.current) {
            return;
          }
          setIsLoading(false);
          setEstimate(x);
          setError(null);
          onEstimateChangeRef.current(x);
        })
        .catch(x => {
          if (sequence !== requestSequenceRef.current) {
            return;
          }
          setIsLoading(false);
          setEstimate(null);
          setError(getPushCampaignFailureMessage(x, 'Could not estimate the audience.'));
          onEstimateChangeRef.current(null);
        });
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [requestKey, needsSavedDraft]);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <Users className="h-4 w-4" />
        Estimated audience
      </div>

      {needsSavedDraft && (
        <p className="mt-2 text-sm text-gray-500">
          Save the draft to estimate the test list.
        </p>
      )}

      {!needsSavedDraft && isLoading && (
        <p className="mt-2 text-sm text-gray-500">Calculating...</p>
      )}

      {!needsSavedDraft && !isLoading && error != null && (
        <p className="mt-2 text-sm text-orange-700">{error}</p>
      )}

      {!needsSavedDraft && !isLoading && error == null && estimate != null && (
        <div className="mt-2">
          <div className="text-3xl font-semibold text-gray-900">{estimate.reachable}</div>
          <div className="text-sm text-gray-600">
            reachable of {estimate.total} in the segment
          </div>
          {estimate.approximate ? (
            <p className="mt-2 text-xs text-gray-500">
              The audience is small, the breakdown is not shown and the number is approximate.
            </p>
          ) : (
            <dl className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-600 sm:grid-cols-3 lg:grid-cols-5">
              <div>
                <dt>Not active</dt>
                <dd className="text-sm font-medium text-gray-900">{estimate.not_active ?? 0}</dd>
              </div>
              <div>
                <dt>No email</dt>
                <dd className="text-sm font-medium text-gray-900">{estimate.no_email ?? 0}</dd>
              </div>
              <div>
                <dt>No device</dt>
                <dd className="text-sm font-medium text-gray-900">{estimate.no_device ?? 0}</dd>
              </div>
              <div>
                <dt>Frequency capped</dt>
                <dd className="text-sm font-medium text-gray-900">{estimate.frequency_capped ?? 0}</dd>
              </div>
              {estimate.opted_out != null && (
                <div>
                  <dt>Opted out</dt>
                  <dd className="text-sm font-medium text-gray-900">{estimate.opted_out}</dd>
                </div>
              )}
            </dl>
          )}
          {estimate.reachable === 0 && (
            <p className="mt-2 text-sm text-red-700">
              Nobody can be reached with these settings, the campaign cannot be launched.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
